import type { FC, SVGProps } from 'react';
import mcpChatGpt from '../../../../assets/images/icons/integrations/ic-integration-mcp-chatgpt.svg';
import mcpClaude from '../../../../assets/images/icons/integrations/ic-integration-mcp-claude.svg';
import mcpCline from '../../../../assets/images/icons/integrations/ic-integration-mcp-cline.svg';
import mcpCursor from '../../../../assets/images/icons/integrations/ic-integration-mcp-cursor.svg';
import mcpGemini from '../../../../assets/images/icons/integrations/ic-integration-mcp-gemini.svg';
import mcpVsCode from '../../../../assets/images/icons/integrations/ic-integration-mcp-vscode.svg';
import mcpWindsurf from '../../../../assets/images/icons/integrations/ic-integration-mcp-windsurf.svg';
import mcpZed from '../../../../assets/images/icons/integrations/ic-integration-mcp-zed.svg';
import mcpAntigravity from '../../../../assets/images/icons/integrations/ic-integration-mcp-antigravity.png';
import mcpOtherAiTools from '../../../../assets/images/icons/integrations/ic-integration-mcp-otheraitools.svg';
import basecamp from '../../../../assets/images/icons/integrations/ic-integration-basecamp.png';
import github from '../../../../assets/images/icons/integrations/ic-integration-github.png';
import slack from '../../../../assets/images/icons/integrations/ic-integration-slack.png';
import linear from '../../../../assets/images/icons/integrations/ic-integration-linear.svg';
import trellonative from '../../../../assets/images/icons/integrations/ic-integration-trello-native.svg';
import jiranative from '../../../../assets/images/icons/integrations/ic-integration-jira-native.svg';
import zapier from '../../../../assets/images/icons/integrations/ic-integration-zapier.png';
import asananative from '../../../../assets/images/icons/integrations/ic-integration-asana-native.svg';
import customproductive from '../../../../assets/images/icons/integrations/ic-integration-productiveio.png';
import clickupnative from '../../../../assets/images/icons/integrations/ic-integration-clickup-native.svg';
import mondaycom from '../../../../assets/images/icons/integrations/ic-integration-monday.png';
import webhooks from '../../../../assets/images/icons/integrations/ic-integration-webhooks.png';
import harvest from '../../../../assets/images/icons/integrations/ic-integration-harvest.png';
import teamwork from '../../../../assets/images/icons/integrations/ic-integration-teamwork.png';

export type IntegrationKind = 'standard' | 'mcp';

// TODO(integrations_cleanup): category should eventually be owned by the backend alongside the integration catalog.
export type IntegrationCategory =
  | 'project_management'
  | 'communication'
  | 'dev_tools'
  | 'ai_mcp'
  | 'other';

export type SvgIconComponent = FC<SVGProps<SVGSVGElement>>;

export type IntegrationInfo = {
  kind?: IntegrationKind;
  category?: IntegrationCategory;
  articleId?: number;
  icon?: string | SvgIconComponent;
  description?: string;
  label?: string;
  link?: string;
  video?: boolean;
};

export const integrationsInfo: Record<string, IntegrationInfo> = {
  basecamp: {
    category: 'project_management',
    articleId: 33886,
    icon: basecamp,
    description:
      'Create To-do list items for each bug in a nominated To-do list and keep them in sync.',
    label: 'Basecamp'
  },
  github: {
    category: 'dev_tools',
    articleId: 33875,
    icon: github,
    description:
      'Keep your bug reports in sync by creating GitHub issues in your repository.',
    label: 'GitHub'
  },
  slack_legacy: {
    category: 'communication',
    articleId: 84823,
    icon: slack,
    description:
      'Send a variety of feedback and bug reports directly to the Slack channel of your choice.',
    label: 'Slack (Legacy)'
  },
  slack: {
    category: 'communication',
    articleId: 84823,
    icon: slack,
    description:
      'Send a variety of feedback and bug reports directly to the Slack channel of your choice.',
    label: 'Slack'
  },
  trellonative: {
    category: 'project_management',
    articleId: 11430565,
    icon: trellonative,
    description:
      'Send bugs and website feedback reported in BugHerd directly to Trello.',
    label: 'Trello'
  },
  zapier: {
    category: 'dev_tools',
    articleId: 33883,
    icon: zapier,
    description:
      'Connect with more than 1500+ tools and services you already use (including JIRA, Trello and Asana) via Zapier.',
    label: 'Zapier'
  },
  asananative: {
    category: 'project_management',
    link:
      'https://www.loom.com/share/c04c2e3d67624614a4c9a4555f4fb82d?sid=9258c1e9-bd1d-4d9d-ac1e-afb1e1e03746',
    icon: asananative,
    video: true,
    description:
      'Track bugs and report website feedback in Asana by integrating with BugHerd.',
    label: 'Asana'
  },
  productivenative: {
    category: 'project_management',
    link: 'https://youtu.be/cT5_XanbrSo',
    icon: customproductive,
    video: true,
    description:
      'Send bugs and website feedback reported in BugHerd directly to Productive.',
    label: 'Productive'
  },
  clickupnative: {
    category: 'project_management',
    articleId: 11430592,
    link:
      'https://www.loom.com/share/630878df52ff4cf0ba2e37131b9c4784?sid=84d911f9-b58f-4b8c-8c74-25a90cda24de',
    icon: clickupnative,
    video: true,
    description:
      'Track bugs and report website feedback in ClickUp by integrating with BugHerd.',
    label: 'ClickUp'
  },
  jiranative: {
    category: 'project_management',
    link:
      'https://www.loom.com/share/52407f1ba8284af0ab478e31985a8e3a?sid=3dd07b71-d59c-46f1-8c73-5cec7ecc4ef6',
    icon: jiranative,
    video: true,
    description:
      'Send bugs and feedback tasks seamlessly from BugHerd into Jira.',
    label: 'Jira'
  },
  mondaynative: {
    category: 'project_management',
    link:
      'https://www.loom.com/share/11eb1231f2bd4872ae0b2ccd423c7c6f?sid=5408c7dc-c2ff-4d86-873c-e7b997427f1a',
    icon: mondaycom,
    video: true,
    description:
      'Track bugs and report website feedback in Monday.com by integrating with BugHerd.',
    label: 'Monday.com'
  },
  linearnative: {
    category: 'project_management',
    link: 'https://youtu.be/hp1GAZDhVRw',
    icon: linear,
    video: true,
    description:
      'Send bugs and website feedback reported in BugHerd directly to Linear.',
    label: 'Linear'
  },
  webhooks: {
    category: 'dev_tools',
    link: 'https://www.bugherd.com/api_v2#api_webhook_list',
    icon: webhooks,
    description: 'Create your own bespoke integration using webhooks.',
    label: 'Webhooks'
  },
  harvest: {
    category: 'other',
    articleId: 84964,
    icon: harvest,
    description:
      'Track the time you spend on tasks in all your BugHerd projects and allocate it to Client Projects in Harvest.',
    label: 'Harvest'
  },
  teams: {
    category: 'communication',
    link:
      'https://www.loom.com/share/a7a6bd4f5bbc4c39afb48d1003d6d864?sid=d922d08a-712b-4c23-97d4-2ec6d53129b3',
    video: true,
    description:
      'Send a variety of feedback and bug reports directly to the Microsoft Teams channel of your choice.',
    label: 'Microsoft Teams'
  },
  teamwork: {
    category: 'project_management',
    link: 'https://player.vimeo.com/video/1038088896',
    video: true,
    icon: teamwork,
    description:
      'Send feedback and bugs reported in BugHerd directly to Teamwork.',
    label: 'Teamwork'
  },

  // MCP integrations — allow users to install the BugHerd MCP into AI coding tools.
  mcp_chatgpt: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835258,
    icon: mcpChatGpt,
    description: "Connect BugHerd's MCP to manage your project from ChatGPT.",
    label: 'ChatGPT'
  },
  mcp_codexcli: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835262,
    icon: mcpChatGpt,
    description: "Connect BugHerd's MCP to manage your project from Codex CLI.",
    label: 'Codex CLI'
  },
  mcp_claudecode: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835255,
    icon: mcpClaude,
    description: "Connect BugHerd's MCP to manage your project from Claude Code.",
    label: 'Claude Code'
  },
  mcp_claudeai: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835961,
    icon: mcpClaude,
    description: "Connect BugHerd's MCP to manage your project from Claude.ai.",
    label: 'Claude.ai'
  },
  mcp_cline: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835261,
    icon: mcpCline,
    description: "Connect BugHerd's MCP to manage your project from Cline.",
    label: 'Cline'
  },
  mcp_cursor: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835263,
    icon: mcpCursor,
    description: "Connect BugHerd's MCP to manage your project from Cursor.",
    label: 'Cursor'
  },
  mcp_geminicli: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835265,
    icon: mcpGemini,
    description: "Connect BugHerd's MCP to manage your project from Gemini CLI.",
    label: 'Gemini CLI'
  },
  mcp_githubcopilot: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835266,
    icon: github,
    description: "Connect BugHerd's MCP to manage your project from GitHub CoPilot.",
    label: 'GitHub Copilot'
  },
  mcp_vscodecopilot: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835268,
    icon: mcpVsCode,
    description: "Connect BugHerd's MCP to manage your project from VS Code CoPilot.",
    label: 'VS Code Copilot'
  },
  mcp_windsurf: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835272,
    icon: mcpWindsurf,
    description: "Connect BugHerd's MCP to manage your project from Windsurf.",
    label: 'Windsurf'
  },
  mcp_zed: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835274,
    icon: mcpZed,
    description: "Connect BugHerd's MCP to manage your project from Zed.",
    label: 'Zed'
  },
  mcp_antigravity: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 15092969,
    icon: mcpAntigravity,
    description: "Connect BugHerd's MCP to manage your project from Antigravity.",
    label: 'Antigravity'
  },
  mcp_otheraitools: {
    kind: 'mcp',
    category: 'ai_mcp',
    articleId: 14835255,
    icon: mcpOtherAiTools,
    description: "Connect BugHerd's MCP to your preferred AI tool.",
    label: 'Other AI tools'
  }
};

const connectorCatalogKeys: Record<string, string> = {
  bcx: 'basecamp',
  team: 'teams',
  jira: 'jiranative',
  asana: 'asananative',
  clickup: 'clickupnative',
  trello: 'trellonative',
  monday: 'mondaynative',
  productive: 'productivenative',
  linear: 'linearnative'
};

export const integrationCatalogKey = (code: string): string => {
  const normalized = code.replace(/\./g, '');

  return connectorCatalogKeys[normalized] || normalized;
};

export const integrationInfoForCode = (code: string): IntegrationInfo =>
  integrationsInfo[integrationCatalogKey(code)] || {};

export const integrationEntriesForKind = (
  kind: IntegrationKind
): [string, IntegrationInfo][] =>
  Object.entries(integrationsInfo).filter(
    ([, integrationInfo]) => integrationInfo.kind === kind
  );
